import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useServices, useSession } from "../app/services";
import { Icon, type IconName } from "../components/Icon";
import { Ribatan } from "../components/stage";
import { Dialog, PageHeader } from "../components/ui";
import { rememberGuide } from "../components/WelcomeGuide";
import { cancelTitleFocus } from "../app/focus";

const STEPS: Array<{ label: string; title: string; icon: IconName; body: string; tip: string }> = [
  { label: "集まる", title: "まずは、DiscordのVCへ。", icon: "speaker",
    body: "ほろりばのDiscordで、音楽を聴きたいVC（ボイスチャンネル）に入りましょう。Botがすでに再生しているときは、Botと同じVCへ。",
    tip: "音楽が流れるのはDiscordです。Webは再生状況を見たり、好きな曲を整理したりする場所です。" },
  { label: "聴く", title: "聴きたい曲を、ひとつ追加。", icon: "plus",
    body: "Discordの音楽パネルで「曲を追加」を押し、YouTubeのURLや曲名を入れます。同じVCにいれば、みんなが追加できます。",
    tip: "先に歌唱を登録する必要はありません。ほかの曲が流れていたら、キューに入り順番に再生されます。" },
  { label: "残す", title: "その瞬間に、「ここ好き」。", icon: "heart",
    body: "好きなフレーズが来たら、DiscordのパネルかWebの「いま聴く」で「ここ好き」を押してみてください。Botが実際に流している位置を、あなたの下書きに残します。",
    tip: "押しただけでは公開されません。Discordでは、そこから「この箇所から歌唱を登録」もできます。登録は残しておきたいときだけでOK。" },
  { label: "育てる", title: "好きな瞬間を、自分の棚へ。", icon: "library",
    body: "Webの「ライブラリ」から、ここ好きの下書きを開きます。位置やひとことを整えて自分用に保存したり、メンバーに公開したりできます。",
    tip: "お気に入りやプレイリストもここへ。公開したここ好きやおすすめは「見つける」に並び、みんなの次の一曲につながります。" },
];

export function GuidePage() {
  const [step, setStep] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const previous = useRef(0);
  const session = useSession();
  const { api } = useServices();
  const signedIn = session.status === "signed_in";
  const item = STEPS[step];
  useEffect(() => {
    if (previous.current !== step) { cancelTitleFocus(); heading.current?.focus(); }
    previous.current = step;
  }, [step]);
  return <div className="page guide-page">
    <PageHeader title="はじめてのほろりばレコード" lead="好きな曲を、一緒に。聴くだけでも、好きな瞬間を残しても。" />
    <div className="guide-intro">
      <Ribatan pose="hello" sizes="100px" />
      <p><strong>りばたんと、4つのステップ。</strong><span>気になるところから、いつでも読み返せます。</span></p>
    </div>
    <nav aria-label="使い方のステップ" className="guide-steps">
      <ol>{STEPS.map((s, i) => <li key={s.label}>
        <button type="button" aria-current={step === i ? "step" : undefined} onClick={() => setStep(i)}>
          <span className="guide-steps__number">{i + 1}</span>{s.label}
        </button>
      </li>)}</ol>
    </nav>
    <section className="guide-card" aria-labelledby="guide-step-title">
      <GuideMedia key={step} step={step} />
      <div className="guide-copy">
        <p className="guide-eyebrow">STEP {String(step + 1).padStart(2, "0")} / 04</p>
        <h2 id="guide-step-title" tabIndex={-1} ref={heading}>{item.title}</h2>
        <p>{item.body}</p>
        <p className="guide-tip"><Icon name={item.icon} /><span>{item.tip}</span></p>
      </div>
      <div className="guide-actions">
        <button type="button" className="btn btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)}>前へ</button>
        {step < 3 ? <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)}>次へ <span aria-hidden="true">→</span></button>
          : signedIn ? <Link className="btn btn-primary" to="/now" onClick={rememberGuide}>いま聴くへ</Link>
            : <a className="btn btn-primary" href={api.loginUrl() || "/"} onClick={rememberGuide}>Discordでログイン</a>}
      </div>
    </section>
    <p className="guide-later"><Link to={signedIn ? "/now" : "/"} onClick={rememberGuide}>あとで読む</Link><span>「使い方」からいつでも戻れます。</span></p>
    <section className="guide-faq section" aria-labelledby="guide-questions">
      <h2 id="guide-questions" className="section-title">ちょっと迷ったら</h2>
      <details><summary>曲の追加と、歌唱の登録は何が違う？</summary><p>追加は「今から聴く」ための操作です。登録は歌っている区間や歌った人を残して、あとから見つけやすくするための操作。まずはDiscordで追加するだけで楽しめます。</p></details>
      <details><summary>音が聞こえないときは？</summary><p>DiscordでBotと同じVCに入り、スピーカーミュートやBot個別の音量を確認してください。Webだけを開いても音は流れません。Botのエラーが出ているときは、表示された案内を確認してから再度操作してください。</p></details>
      <details><summary>DJ Mixって何？</summary><p>今の曲を起点に、関連する曲を自動でつないでいく機能です。直近と違うアーティストも混ぜながら選びます。DJ・管理者がオンにでき、みんなが追加した曲を優先して流します。</p></details>
      <details><summary>ここ好きを公開すると、誰に見える？</summary><p>投稿ページはほろりばのサーバーメンバー限定です。投稿の「共有する」からリンクをコピーできます。共有リンクのプレビューには本文・曲名・時刻が表示され、サーバー外からも取得できます。投稿者名やアイコンは出ません。下書きや自分用の保存は、自分だけのものです。</p></details>
      <details><summary>歌った人の名前が違っていたら？</summary><p>最初はYouTubeのチャンネル名を候補として使います。歌唱を登録するときに直せます。登録後は、登録した人や管理者が曲情報の編集から修正できます。</p></details>
      <details><summary>ホーム画面に追加したい</summary><p>iPhoneではブラウザの共有メニューから「ホーム画面に追加」、Androidやパソコンではブラウザの「アプリをインストール」などを選んでください。対応ブラウザでは、ほろりばレコードのアイコンから開けます。利用にはインターネット接続が必要です。</p></details>
    </section>
  </div>;
}

const GUIDE_SHOTS = [
  { src: "/guide/discord-voice-v1.jpg", width: 286, height: 99, source: "Discord", title: "VCの入口",
    alt: "Discordのボイスチャット欄に、音楽パネルとVC1が並んでいる画面",
    caption: "サイドバーからVCへ。Botがいるときは、パネルに表示されたVCと同じ場所に入ります。" },
  { src: "/guide/discord-add-v1.jpg", width: 480, height: 310, source: "Discord", title: "曲を追加する画面",
    alt: "曲を探すフォーム。曲名やYouTubeのURLの欄に「さくらみこ」を入力した状態",
    caption: "「曲を追加」から、この入力欄へ。URLか曲名を入れて送信します。端末の言語によって送信ボタンはSubmitと表示されます。" },
  { src: "/guide/discord-controls-v1.jpg", width: 600, height: 211, source: "Discord", title: "ここ好きのボタン",
    alt: "Discordの操作パネル。2段目の左に「ここ好き」がある画面",
    caption: "2段目の左にある「ここ好き」を押します。再生している瞬間が下書きに残ります。" },
  { src: "/guide/web-moment-v1.jpg", width: 640, height: 658, source: "Web", title: "ここ好きの編集画面",
    alt: "記録した位置、時刻の微調整、ひとこと、自分用に保存とメンバーに公開のボタンがある編集画面",
    caption: "Webの「ここ好き」は記録が完了すると、この編集画面が開きます。Discordで記録したものは「ライブラリ」→「ここ好き」→「整える」へ。公開すると投稿ページに移ります。" },
];

function GuideMedia({ step }: { step: number }) {
  const [expanded, setExpanded] = useState(false);
  const [failed, setFailed] = useState(false);
  const shot = GUIDE_SHOTS[step];
  return <div className="guide-media">
    <figure>
      <div className="guide-media__meta"><span>{shot.source}の実際の画面</span><span>2026.10 撮影</span></div>
      {failed ? <p className="notice">画像を読み込めませんでした。このページの説明文で手順を確認できます。</p>
        : <button type="button" className="guide-media__open" aria-label={`${shot.title}を拡大`} onClick={() => setExpanded(true)}>
          <img src={shot.src} width={shot.width} height={shot.height} alt={shot.alt} onError={() => setFailed(true)} />
          <span className="guide-media__zoom" aria-hidden="true"><Icon name="search" />拡大する</span>
        </button>}
      <figcaption>{shot.caption}</figcaption>
    </figure>
    {step !== 3 && <a className="guide-media__overview" href="/guide/discord-panel-v1.jpg" target="_blank" rel="noreferrer">音楽パネル全体を見る<span className="visually-hidden">（新しいタブ）</span> ↗</a>}
    <Dialog open={expanded} title={shot.title} onClose={() => setExpanded(false)}>
      <div className="guide-media__pan" role="region" aria-label="拡大したスクリーンショット" tabIndex={0}>
        <img className="guide-media__large" src={shot.src} width={shot.width} height={shot.height} style={{ minWidth: shot.width }} alt={shot.alt} />
      </div>
      <p className="guide-media__description">{shot.caption}</p>
    </Dialog>
  </div>;
}
